'use client';

import { useContext, useMemo, useState } from 'react';
import VendingMachineDataContext from '~/context/vm/VendingMachineDataContext';
import ExpandButton from './ExpandButton';
import {
  InternalLinkModOverview,
  InternalModLinks,
  InternalLinkModTitle,
  InternalLinkModArea,
  InternalLinkWrapper,
  InternalLinkStyle
} from './InternalLinkMod.styles';

function useToggleExpand(): [boolean, () => void] {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = () => setIsExpanded(!isExpanded);

  return [isExpanded, toggle];
}

type Link = {
  link: string;
  text: string;
};

function useVendingMachineLinks(): Link[] {
  const { vendingMachineLocations } = useContext(VendingMachineDataContext);

  return useMemo<Link[]>(
    () =>
      vendingMachineLocations.map(x => ({
        text: `Vending Machine in ${x.city}, ${x.state}`,
        link: `/vending-machine/${x.city.replace(/\s+/g, '-')}-${
          x.state
        }`.toLowerCase()
      })),
    [vendingMachineLocations]
  );
}

const maxLinksToShow = 9;

export default function InternalLinkMod() {
  const [isExpanded, toggle] = useToggleExpand();
  const links = useVendingMachineLinks();
  const numberOfLinksToShow = isExpanded ? links.length : maxLinksToShow;
  const linksToShow = links.slice(0, numberOfLinksToShow);
  const showViewMoreButton = links.length > maxLinksToShow;

  return links.length ? (
    <InternalLinkModOverview>
      <InternalModLinks>
        <InternalLinkModTitle>View Vending Machines</InternalLinkModTitle>
        <InternalLinkModArea>
          {linksToShow.map(({ link, text }) => (
            <InternalLinkWrapper key={link}>
              <InternalLinkStyle
                data-analytics-id={text}
                href={link}
                data-testid="vending-machine-link"
              >
                {text}
              </InternalLinkStyle>
            </InternalLinkWrapper>
          ))}
        </InternalLinkModArea>

        {showViewMoreButton && (
          <ExpandButton toggleExpand={toggle} isExpanded={isExpanded} />
        )}
      </InternalModLinks>
    </InternalLinkModOverview>
  ) : null;
}
